import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Loader2, Package } from "lucide-react";
import { api, formatINR, priceForProduct, productUrl } from "../lib/api";

/**
 * ProductSearchAutocomplete
 *
 * Debounced live-suggestion dropdown for the global product catalog.
 * - Word-skipping search (server-side AND-token match)
 * - Shows: vendor logo/thumb, product name, SKU, price
 * - Keyboard: ArrowDown/ArrowUp/Enter/Escape
 * - Click a suggestion → /products/:id
 * - Press Enter (no active suggestion) → /search?q=...
 *
 * Styling can be overridden via `theme = "light" | "dark"` prop.
 */
export default function ProductSearchAutocomplete({
  placeholder = "Search Microsoft, Adobe, Autodesk, Zoho…",
  theme = "dark",
  testIdPrefix = "product-search",
  onSubmit,
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  // Debounced fetch — 250ms after last keystroke, min 2 chars. The dropdown
  // shows the first 8 hits and the true total (X-Total-Count header) so the
  // "See all N matches" CTA at the bottom can render the accurate count.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/products", { params: { q: term, limit: 8 } })
        .then((r) => {
          setItems(Array.isArray(r.data) ? r.data.slice(0, 8) : []);
          // Read the true total (pre-limit) from the backend header. Fall
          // back to result count if the header isn't present (e.g. proxy
          // stripped it) so we never over-promise.
          const hdr = r?.headers?.["x-total-count"] ?? r?.headers?.["X-Total-Count"];
          const parsed = hdr != null ? parseInt(hdr, 10) : NaN;
          setTotal(Number.isFinite(parsed) ? parsed : (Array.isArray(r.data) ? r.data.length : 0));
          setActive(-1);
        })
        .catch(() => { setItems([]); setTotal(0); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    if (active >= 0 && items[active]) {
      go(items[active]);
      return;
    }
    const term = q.trim();
    if (onSubmit) return onSubmit(term);
    if (!term) {
      navigate("/search");
      return;
    }
    // Intent-resolve: ask the backend whether this query is an exact SKU /
    // product name (→ product page), a broad keyword (→ category landing),
    // or generic prose (→ /search results). Feels instant because the box
    // stays visible while we hop — no full-page flash.
    api
      .get("/search/resolve", { params: { q: term } })
      .then((r) => {
        const dest = r?.data?.url;
        if (dest && typeof dest === "string" && dest.startsWith("/")) {
          setOpen(false);
          setQ("");
          setItems([]);
          navigate(dest);
        } else {
          navigate(`/search?q=${encodeURIComponent(term)}`);
        }
      })
      .catch(() => navigate(`/search?q=${encodeURIComponent(term)}`));
  };

  // Explicit "see all matches" bypasses intent routing — user has already
  // decided they want the full multi-vendor result grid.
  const seeAllMatches = () => {
    const term = q.trim();
    setOpen(false);
    navigate(term ? `/search?q=${encodeURIComponent(term)}` : "/search");
  };

  const go = (p) => {
    setOpen(false);
    setQ("");
    setItems([]);
    navigate(productUrl(p));
  };

  const onKey = (e) => {
    if (!open || items.length === 0) {
      if (e.key === "Enter") submit(e);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + items.length) % items.length); }
    else if (e.key === "Enter") { e.preventDefault(); submit(e); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const dark = theme === "dark";
  const inputCls = dark
    ? "w-full h-14 pl-12 pr-28 rounded-sm bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-base focus:outline-none focus:bg-white/15 focus:border-brand-electric transition-colors"
    : "w-full h-14 pl-12 pr-28 rounded-sm bg-white border border-slate-300 text-brand-navy placeholder:text-slate-500 text-base focus:outline-none focus:border-brand-blue transition-colors";

  const iconCls = dark ? "text-white/60" : "text-slate-500";
  const btnCls = dark
    ? "absolute right-2 top-1/2 -translate-y-1/2 rounded-sm bg-brand-electric text-brand-navy px-4 h-10 text-sm font-bold hover:bg-brand-electric/90 transition-colors inline-flex items-center gap-1.5"
    : "absolute right-2 top-1/2 -translate-y-1/2 rounded-sm bg-brand-blue text-white px-4 h-10 text-sm font-bold hover:bg-brand-blueDark transition-colors inline-flex items-center gap-1.5";

  return (
    <div className="relative" ref={boxRef}>
      <form onSubmit={submit} role="search" aria-label="Search products">
        <div className="relative">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none ${iconCls}`} />
          <input
            type="search"
            data-testid={`${testIdPrefix}-input`}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder={placeholder}
            className={inputCls}
            autoComplete="off"
            aria-autocomplete="list"
            aria-controls={`${testIdPrefix}-suggestions`}
            aria-expanded={open && items.length > 0}
          />
          <button
            type="submit"
            data-testid={`${testIdPrefix}-submit`}
            className={btnCls}
          >
            Search <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </form>

      {open && q.trim().length >= 2 && (
        <div
          id={`${testIdPrefix}-suggestions`}
          role="listbox"
          data-testid={`${testIdPrefix}-dropdown`}
          className="absolute left-0 right-0 top-full mt-2 rounded-md border border-slate-200 bg-white shadow-2xl z-40 overflow-hidden"
        >
          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-4 py-4 text-sm text-slate-500" data-testid={`${testIdPrefix}-empty`}>
              No products found for <span className="font-semibold text-brand-navy">"{q.trim()}"</span>. Try a different keyword or{" "}
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/products"); }}
                data-testid={`${testIdPrefix}-empty-browse`}
                className="text-brand-blue font-semibold underline hover:text-brand-blueDark"
              >
                browse All Categories
              </button>
              .
            </div>
          )}

          {!loading && items.length > 0 && (
            <ul className="max-h-[24rem] overflow-y-auto divide-y divide-slate-100">
              {items.map((p, i) => (
                <SuggestionRow
                  key={p.id}
                  product={p}
                  active={i === active}
                  onHover={() => setActive(i)}
                  onSelect={() => go(p)}
                  testId={`${testIdPrefix}-item-${i}`}
                />
              ))}
              {/* "See all N matches" CTA — distinctly styled row that
                  navigates to /search?q=... showing all matches (not just
                  the top 8). Total comes from X-Total-Count header on the
                  same request, so no extra round trip. */}
              <li>
                <button
                  type="button"
                  onClick={seeAllMatches}
                  data-testid={`${testIdPrefix}-see-all`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-brand-blue/5 hover:bg-brand-blue hover:text-white border-t-2 border-brand-blue text-brand-blue transition-colors"
                >
                  <span className="text-sm font-bold">
                    See all <span data-testid={`${testIdPrefix}-total`} className="tabular-nums">{Math.max(total, items.length)}</span> match{Math.max(total, items.length) === 1 ? "" : "es"} for <span className="italic">"{q.trim()}"</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionRow({ product, active, onHover, onSelect, testId }) {
  const price =
    product.price_yearly ||
    product.price_monthly ||
    priceForProduct(product, "ESD") ||
    product.list_price ||
    0;
  const isPerUser =
    product.product_type === "m365" ||
    product.product_type === "ms_subscription" ||
    product.product_type === "zoho" ||
    product.product_type === "adobe" ||
    product.product_type === "autodesk";
  // Show "Price on request" instead of ₹0 so a missing/unseeded price row
  // never reads as a genuine ₹0 offer to the shopper.
  const hasPrice = Number(price) > 0;
  const priceLabel = hasPrice
    ? (isPerUser ? `${formatINR(price)}/user/mo` : formatINR(price))
    : "Price on request";

  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={active}
        onMouseEnter={onHover}
        onClick={onSelect}
        data-testid={testId}
        className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition-colors ${
          active ? "bg-brand-blue/8" : "hover:bg-slate-50"
        }`}
      >
        <div className="shrink-0 h-10 w-10 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
          {product.image ? (
            <img src={product.image} alt="" width="48" height="48" decoding="async" className="max-w-full max-h-full object-contain" loading="lazy" />
          ) : (
            <Package className="h-5 w-5 text-slate-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {/* Product name — wraps to 2 lines instead of truncating with an
              ellipsis. Truncation was cutting names to <10 chars in the
              narrow header dropdown ("Adobe F...", "Substa..."). Two-line
              clamp keeps the row height bounded (~2.6rem) while still
              showing the full identifiable product name. */}
          <div className="font-display font-semibold text-sm text-brand-navy leading-snug line-clamp-2 break-words" title={product.name}>
            {product.name}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] font-mono text-slate-500">
            <span className="uppercase tracking-widest">{product.category || product.product_type}</span>
            {product.sku && (
              <span className="text-slate-400" title={`SKU ${product.sku}`}>· SKU <span className="text-slate-600">{product.sku}</span></span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-xs font-mono font-bold ${hasPrice ? "text-brand-blue" : "text-slate-500"}`}>{priceLabel}</div>
          <div className="text-[10px] font-mono text-slate-400">{hasPrice ? "excl. GST" : "Contact sales"}</div>
        </div>
      </button>
    </li>
  );
}
